import React, { useCallback, useEffect, useMemo, useState } from "react";
import databaseAPIsService, { DatabaseQueryRow, DatabaseStatusResponse } from "../hooks/databaseAPIs";
import AppLoading from "../components/AppLoading";
import DialogBox from "../components/DialogBox";

type ExtraColumnKey = "id" | "host" | "db" | "command" | "query";
type SortColumnKey = "time" | "user" | "state" | "id" | "host" | "db" | "command" | "summary";
type SortDirection = "asc" | "desc";

const EXTRA_COLUMN_LABELS: Record<ExtraColumnKey, string> = {
  id: "Connection ID",
  host: "Host",
  db: "Database",
  command: "Command",
  query: "Query preview",
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hours}h ${remMins}m`;
}

function formatUptime(seconds: number): string {
  if (seconds < 3600) return formatDuration(seconds);
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  return `${hours}h`;
}

function truncateText(text: string, max = 56): string {
  const trimmed = text.replace(/\s+/g, " ").trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}…`;
}

function querySummary(row: DatabaseQueryRow): string {
  if (row.info) return truncateText(row.info, 72);
  if (row.command && row.command !== "Query") return row.command;
  return "—";
}

function compareQueries(a: DatabaseQueryRow, b: DatabaseQueryRow, column: SortColumnKey, dir: SortDirection): number {
  const sign = dir === "asc" ? 1 : -1;

  if (column === "time") return (a.timeSec - b.timeSec) * sign;
  if (column === "id") return (a.id - b.id) * sign;

  const valueFor = (row: DatabaseQueryRow): string => {
    switch (column) {
      case "user":
        return row.user;
      case "state":
        return row.state ?? row.command;
      case "host":
        return row.host;
      case "db":
        return row.db ?? "";
      case "command":
        return row.command;
      case "summary":
        return querySummary(row);
      default:
        return "";
    }
  };

  return valueFor(a).localeCompare(valueFor(b), undefined, { sensitivity: "base" }) * sign;
}

const DatabaseManagementPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DatabaseStatusResponse | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [killingIds, setKillingIds] = useState<Set<number>>(new Set());
  const [killingAll, setKillingAll] = useState(false);
  const [extraColumns, setExtraColumns] = useState<Set<ExtraColumnKey>>(new Set());
  const [selectedRow, setSelectedRow] = useState<DatabaseQueryRow | null>(null);
  const [sort, setSort] = useState<{ column: SortColumnKey; dir: SortDirection }>({
    column: "time",
    dir: "desc",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await databaseAPIsService.getStatus();
      setData(response);
      setLastRefreshedAt(Date.now());
    } catch (e: any) {
      setError(e?.message || "Failed to fetch database status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const interval = setInterval(() => void load(), 15000);
    return () => clearInterval(interval);
  }, [load]);

  const stuckQueries = useMemo(
    () => (data?.queries ?? []).filter((q) => q.stuck),
    [data],
  );

  const sortedQueries = useMemo(() => {
    const rows = [...(data?.queries ?? [])];
    rows.sort((a, b) => {
      if (a.stuck !== b.stuck) return a.stuck ? -1 : 1;
      return compareQueries(a, b, sort.column, sort.dir);
    });
    return rows;
  }, [data, sort]);

  const toggleSort = useCallback((column: SortColumnKey) => {
    setSort((prev) =>
      prev.column === column
        ? { column, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { column, dir: column === "time" || column === "id" ? "desc" : "asc" },
    );
  }, []);

  const renderSortableHeader = useCallback(
    (column: SortColumnKey, label: string, className?: string) => {
      const active = sort.column === column;
      return (
        <th
          key={column}
          className={`queue-table-th-sortable ${className ?? ""} ${active ? "sorted" : ""}`}
          onClick={() => toggleSort(column)}
          aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
        >
          <span className="queue-table-th-label">
            {label}
            <span className="queue-table-sort-icon" aria-hidden>
              {active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
            </span>
          </span>
        </th>
      );
    },
    [sort, toggleSort],
  );

  const isExtraColumnEnabled = useCallback(
    (key: ExtraColumnKey) => extraColumns.has(key),
    [extraColumns],
  );

  const toggleExtraColumn = useCallback((key: ExtraColumnKey) => {
    setExtraColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const killConnection = useCallback(
    async (connectionId: number) => {
      if (!window.confirm(`Kill connection ${connectionId}?`)) return;
      try {
        setKillingIds((prev) => new Set(prev).add(connectionId));
        setError(null);
        await databaseAPIsService.killConnection(connectionId);
        setSelectedRow((prev) => (prev?.id === connectionId ? null : prev));
        await load();
      } catch (e: any) {
        setError(e?.message || `Failed to kill connection ${connectionId}`);
      } finally {
        setKillingIds((prev) => {
          const next = new Set(prev);
          next.delete(connectionId);
          return next;
        });
      }
    },
    [load],
  );

  const killAllStuck = useCallback(async () => {
    const count = data?.stuckCount ?? 0;
    if (count === 0) {
      window.alert("No stuck queries to kill.");
      return;
    }
    if (
      !window.confirm(
        `Kill all ${count} stuck quer${count === 1 ? "y" : "ies"} (running ≥ ${data?.stuckThresholdSec ?? 60}s)?`,
      )
    ) {
      return;
    }
    try {
      setKillingAll(true);
      setError(null);
      await databaseAPIsService.killAllStuck();
      setSelectedRow(null);
      await load();
    } catch (e: any) {
      setError(e?.message || "Failed to kill stuck queries");
    } finally {
      setKillingAll(false);
    }
  }, [data, load]);

  const poolLoadPct = useMemo(() => {
    if (!data?.pool?.configured?.max) return 0;
    return Math.round((data.pool.used / data.pool.configured.max) * 100);
  }, [data]);

  const serverLoadPct = useMemo(() => {
    if (!data?.server?.maxConnections) return 0;
    return Math.round((data.server.threadsConnected / data.server.maxConnections) * 100);
  }, [data]);

  const openRowDetails = useCallback((row: DatabaseQueryRow) => {
    setSelectedRow(row);
  }, []);

  return (
    <div className="stats-page database-management-page">
      <div className="layout-header">
        <div className="header-content">
          <h2>Database Management</h2>
          <p>
            Live MySQL processlist and pool stats for tenant{" "}
            <span className="mono">{data?.tenantId ?? "—"}</span>
          </p>
        </div>
        <div className="queue-controls">
          <button
            className="btn btn-danger"
            onClick={() => void killAllStuck()}
            disabled={loading || killingAll || (data?.stuckCount ?? 0) === 0}
            title="Kill all queries running longer than the stuck threshold"
          >
            {killingAll ? "Killing..." : `Kill all stuck (${data?.stuckCount ?? 0})`}
          </button>
          <button className="btn btn-secondary" onClick={() => void load()} disabled={loading}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="auto-refresh-status">
        <span className="status-indicator">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="status-icon">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Page Auto-refresh: Every 15 seconds
          {lastRefreshedAt ? ` · Last updated ${new Date(lastRefreshedAt).toLocaleTimeString()}` : ""}
        </span>
        {data && (
          <div className={`queue-health-status ${data.stuckCount > 0 ? "down" : "up"}`}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {data.stuckCount > 0 ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              )}
            </svg>
            Stuck queries: <span className="queue-health-text">{data.stuckCount}</span>
            {" · "}
            Threshold: {data.stuckThresholdSec}s
          </div>
        )}
      </div>

      {loading && !data && (
        <div className="loading-container">
          <AppLoading />
        </div>
      )}

      {error && (
        <div className="error-container">
          <div className="error-card">
            <div className="error-content">
              <h2>Error</h2>
              <p>{typeof error === "string" ? error : JSON.stringify(error)}</p>
              <button onClick={() => void load()} className="retry-btn">
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div className="layout-content">
          <div className="cache-overview-section">
            <div className="section-header">
              <h2>Overview</h2>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-header">
                    <div className="stat-icon">
                      <div className="icon-container blue">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z" />
                        </svg>
                      </div>
                    </div>
                    <div className="stat-info">
                      <dl>
                        <dt>Pool in use ({poolLoadPct}% of max)</dt>
                        <dd>
                          {data.pool.used} / {data.pool.configured.max}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-header">
                    <div className="stat-icon">
                      <div className="icon-container green">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="stat-info">
                      <dl>
                        <dt>Free / pending · min {data.pool.configured.min}</dt>
                        <dd>
                          {data.pool.free} free · {data.pool.pendingAcquires} pending
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-header">
                    <div className="stat-icon">
                      <div className="icon-container purple">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="stat-info">
                      <dl>
                        <dt>Server connections ({serverLoadPct}% load)</dt>
                        <dd>
                          {data.server.threadsConnected} / {data.server.maxConnections}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-header">
                    <div className="stat-icon">
                      <div className="icon-container orange">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="stat-info">
                      <dl>
                        <dt>
                          Server health · uptime {formatUptime(data.server.uptimeSec)}
                        </dt>
                        <dd>MySQL {data.server.version || "—"}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="keys-section">
            <div className="section-header">
              <h2>
                Active & stuck queries ({data.queries.length})
                {stuckQueries.length > 0 ? ` · ${stuckQueries.length} stuck` : ""}
              </h2>
              <div className="queue-controls">
                <button className="btn btn-secondary" onClick={() => void load()} disabled={loading}>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {loading ? "Refreshing..." : "Refresh"}
                </button>
                <details className="queue-columns-picker">
                  <summary className="queue-columns-picker__summary">Columns</summary>
                  <div className="queue-columns-picker__menu">
                    {(Object.keys(EXTRA_COLUMN_LABELS) as ExtraColumnKey[]).map((key) => (
                      <label key={key} className="queue-columns-picker__item">
                        <input
                          type="checkbox"
                          checked={isExtraColumnEnabled(key)}
                          onChange={() => toggleExtraColumn(key)}
                        />
                        <span>{EXTRA_COLUMN_LABELS[key]}</span>
                      </label>
                    ))}
                  </div>
                </details>
              </div>
            </div>

            {data.queries.length === 0 ? (
              <div className="queue-empty">No active queries on the database.</div>
            ) : (
              <div className="queue-table-scroll">
                <table className="queue-table database-table">
                  <thead>
                    <tr>
                      {renderSortableHeader("time", "Time", "database-col-time")}
                      {renderSortableHeader("user", "User", "database-col-user")}
                      {renderSortableHeader("state", "State", "database-col-state")}
                      {isExtraColumnEnabled("id") && renderSortableHeader("id", "ID", "database-col-id")}
                      {isExtraColumnEnabled("host") && renderSortableHeader("host", "Host", "database-col-host")}
                      {isExtraColumnEnabled("db") && renderSortableHeader("db", "DB", "database-col-db")}
                      {isExtraColumnEnabled("command") && renderSortableHeader("command", "Command", "database-col-command")}
                      {renderSortableHeader("summary", "Summary", "database-col-summary")}
                      {isExtraColumnEnabled("query") && (
                        <th className="database-col-query">Query</th>
                      )}
                      <th className="queue-table-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedQueries.map((row) => (
                      <tr
                        key={row.id}
                        className={`database-row ${row.stuck ? "database-row-stuck" : ""}`}
                        onClick={() => openRowDetails(row)}
                      >
                        <td>
                          <span className={row.stuck ? "database-time-stuck" : ""}>{formatDuration(row.timeSec)}</span>
                          {row.stuck && <span className="database-stuck-badge">Stuck</span>}
                        </td>
                        <td className="queue-table-cell-truncate" title={row.user}>
                          {row.user}
                        </td>
                        <td className="queue-table-cell-truncate" title={row.state ?? row.command}>
                          {row.state ?? row.command}
                        </td>
                        {isExtraColumnEnabled("id") && (
                          <td className="mono">{row.id}</td>
                        )}
                        {isExtraColumnEnabled("host") && (
                          <td className="mono queue-table-cell-truncate" title={row.host}>
                            {row.host}
                          </td>
                        )}
                        {isExtraColumnEnabled("db") && (
                          <td className="queue-table-cell-truncate">{row.db ?? "—"}</td>
                        )}
                        {isExtraColumnEnabled("command") && (
                          <td className="queue-table-cell-truncate">{row.command}</td>
                        )}
                        <td
                          className="database-summary-cell queue-table-cell-truncate"
                          title={row.info ?? undefined}
                        >
                          <span className="database-summary-link">{querySummary(row)}</span>
                        </td>
                        {isExtraColumnEnabled("query") && (
                          <td
                            className="database-query-preview queue-table-cell-truncate"
                            title={row.info ?? undefined}
                          >
                            {row.info ? truncateText(row.info, 80) : "—"}
                          </td>
                        )}
                        <td className="queue-actions" onClick={(e) => e.stopPropagation()}>
                          {row.killable ? (
                            <button
                              type="button"
                              className="delete-key-btn"
                              title="Kill connection"
                              disabled={killingIds.has(row.id) || loading}
                              onClick={() => void killConnection(row.id)}
                            >
                              {killingIds.has(row.id) ? (
                                <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                              ) : (
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              )}
                            </button>
                          ) : (
                            <span className="database-not-killable">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <DialogBox
        isOpen={selectedRow != null}
        onClose={() => setSelectedRow(null)}
        className="database-query-dialog-box"
        width="min(920px, 96vw)"
        height="min(560px, 88vh)"
      >
        {selectedRow && (
          <div className="database-query-dialog">
            <div className="database-query-dialog__header">
              <h3>Connection {selectedRow.id}</h3>
              {selectedRow.stuck && <span className="database-stuck-badge">Stuck</span>}
            </div>

            <dl className="database-query-dialog__meta">
              <div>
                <dt>Time</dt>
                <dd>{formatDuration(selectedRow.timeSec)}</dd>
              </div>
              <div>
                <dt>User</dt>
                <dd>{selectedRow.user}</dd>
              </div>
              <div>
                <dt>Host</dt>
                <dd className="mono">{selectedRow.host}</dd>
              </div>
              <div>
                <dt>Database</dt>
                <dd>{selectedRow.db ?? "—"}</dd>
              </div>
              <div>
                <dt>Command</dt>
                <dd>{selectedRow.command}</dd>
              </div>
              <div>
                <dt>State</dt>
                <dd>{selectedRow.state ?? "—"}</dd>
              </div>
            </dl>

            <div className="database-query-dialog__sql">
              <h4>SQL</h4>
              <pre>{selectedRow.info ?? "No query text (non-SELECT command or protected connection)."}</pre>
            </div>

            <div className="database-query-dialog__actions">
              {selectedRow.killable ? (
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={killingIds.has(selectedRow.id) || loading}
                  onClick={() => void killConnection(selectedRow.id)}
                >
                  {killingIds.has(selectedRow.id) ? "Killing..." : "Kill connection"}
                </button>
              ) : (
                <span className="database-not-killable">This connection cannot be killed</span>
              )}
            </div>
          </div>
        )}
      </DialogBox>
    </div>
  );
};

export default DatabaseManagementPage;
